/**
 * ============================================================================
 * TABLE EXPORT — CSV / Excel download for any column-config + rows pair.
 * ============================================================================
 *
 * Pure utilities, no React. Consumed by ExportMenu; usable anywhere else a
 * download of tabular data is needed.
 *
 * COLUMN CONTRACT: exporters need raw text per cell, not ReactNodes — so they
 * take ExportColumn (header + value extractor) rather than DataTableColumn.
 * Build them next to your DataTable columns from the same source data.
 *
 * EXCEL: exceljs is imported dynamically so it stays out of the main bundle
 * (only loaded the first time a user actually exports).
 */

export interface ExportColumn<T> {
  header: string;
  value: (row: T) => string | number;
}

/** Triggers a browser download of the given blob. */
function download(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

/** RFC-4180 CSV escaping: quote when the value contains , " or newline. */
function csvEscape(value: string | number): string {
  const s = String(value);
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function exportToCsv<T>(
  columns: Array<ExportColumn<T>>,
  rows: T[],
  filename: string,
): void {
  const lines = [
    columns.map((c) => csvEscape(c.header)).join(','),
    ...rows.map((r) => columns.map((c) => csvEscape(c.value(r))).join(',')),
  ];
  // BOM so Excel opens UTF-8 CSVs with correct characters (…, ×, etc.)
  const blob = new Blob(['﻿' + lines.join('\r\n')], { type: 'text/csv;charset=utf-8' });
  download(blob, `${filename}.csv`);
}

export async function exportToExcel<T>(
  columns: Array<ExportColumn<T>>,
  rows: T[],
  filename: string,
): Promise<void> {
  const excelMod = await import('exceljs');
  const ExcelJS = excelMod.default || excelMod;
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(filename.slice(0, 31)); // Excel sheet-name limit

  sheet.columns = columns.map((c) => ({
    header: c.header,
    key: c.header,
    width: Math.min(Math.max(c.header.length + 4, 14), 80),
  }));
  sheet.getRow(1).font = { bold: true };

  for (const row of rows) {
    sheet.addRow(columns.map((c) => c.value(row)));
  }

  const buffer = await workbook.xlsx.writeBuffer();
  download(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    `${filename}.xlsx`,
  );
}
